/**
 * Pointer <-> percent mapping for the annotation lightbox, through `object-contain`.
 *
 * The editor deliberately uses a contained full-size source image rather than
 * inheriting the host node's cropped preview. Its overlay uses
 * `preserveAspectRatio="xMidYMid meet"`, the SVG spelling of `object-contain`.
 * Forward mapping (percent -> user units on a `viewBox="0 0 Wn Hn"`) therefore
 * needs no adaptation:
 *
 *     x_user = x_pct / 100 * Wn
 *
 * The inverse accounts for any letterboxing defensively. The lightbox surface
 * normally shrink-wraps the rendered image, so its offsets are zero; keeping the
 * full contain math here makes resize/layout changes safe without reintroducing
 * a crop.
 */

export interface PointerToPercentArgs {
  /** Pointer position, viewport coordinates. */
  clientX: number
  clientY: number
  /** `getBoundingClientRect()` of the overlay element — includes the ReactFlow zoom. */
  rectLeft: number
  rectTop: number
  rectWidth: number
  /** Layout size of the overlay element, i.e. pre-transform CSS pixels. */
  offsetWidth: number
  offsetHeight: number
  /** Intrinsic size of the source image (`img.naturalWidth` / `naturalHeight`). */
  naturalWidth: number
  naturalHeight: number
}

export interface PercentPoint {
  x: number
  y: number
}

/**
 * Clamp to the valid percent range.
 *
 * This is a floating-point boundary guard ONLY. A pointer landing exactly on the
 * right edge can produce `100.00000000000001`, which the renderer rejects (it
 * refuses out-of-range coordinates rather than clamping them, by design). Every
 * point inside the visible box maps inside the source, so this can never mask a
 * genuinely out-of-range value — and it must not be widened into one that could.
 */
function clampPercent(value: number): number {
  if (value < 0) return 0
  if (value > 100) return 100
  return value
}

/**
 * Convert a pointer event position to percent-of-source coordinates.
 *
 * The zoom factor is derived as `rectWidth / offsetWidth` rather than read from
 * the ReactFlow viewport store: a node nested inside a `nodeGroup` carries an
 * additional transform that the store value does not account for, while the
 * ratio of measured-to-layout width absorbs any number of ancestor transforms.
 */
export function pointerToPercent(args: PointerToPercentArgs): PercentPoint {
  const {
    clientX,
    clientY,
    rectLeft,
    rectTop,
    rectWidth,
    offsetWidth,
    offsetHeight,
    naturalWidth,
    naturalHeight,
  } = args

  if (offsetWidth <= 0 || offsetHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    // Nothing sensible to map against — the image has not laid out or loaded.
    return { x: 0, y: 0 }
  }

  const zoom = rectWidth > 0 ? rectWidth / offsetWidth : 1
  // Node-local layout pixels, independent of canvas zoom.
  const localX = (clientX - rectLeft) / zoom
  const localY = (clientY - rectTop) / zoom

  // `object-contain`: scale until the full source fits, letterboxing the short axis.
  const scale = Math.min(offsetWidth / naturalWidth, offsetHeight / naturalHeight)
  const renderedWidth = naturalWidth * scale
  const renderedHeight = naturalHeight * scale
  // Positive on the letterboxed axis — `object-position` defaults to 50% 50%.
  const offsetX = (offsetWidth - renderedWidth) / 2
  const offsetY = (offsetHeight - renderedHeight) / 2

  const imageX = (localX - offsetX) / scale
  const imageY = (localY - offsetY) / scale

  return {
    x: clampPercent((100 * imageX) / naturalWidth),
    y: clampPercent((100 * imageY) / naturalHeight),
  }
}

/**
 * Forward map: percent-of-source -> node-local layout pixels.
 *
 * The SVG overlay does not need this (its `viewBox` + `meet` reproduce the
 * contained image natively), but HTML chrome positioned over the image does —
 * the inline text input has to sit exactly where the baked glyphs will land,
 * and it is a DOM element, not an SVG one.
 */
export function percentToLocal(args: {
  xPct: number
  yPct: number
  offsetWidth: number
  offsetHeight: number
  naturalWidth: number
  naturalHeight: number
}): { left: number; top: number } {
  const { xPct, yPct, offsetWidth, offsetHeight, naturalWidth, naturalHeight } = args
  if (naturalWidth <= 0 || naturalHeight <= 0) return { left: 0, top: 0 }
  const scale = Math.min(offsetWidth / naturalWidth, offsetHeight / naturalHeight)
  const offsetX = (offsetWidth - naturalWidth * scale) / 2
  const offsetY = (offsetHeight - naturalHeight * scale) / 2
  return {
    left: offsetX + (xPct / 100) * naturalWidth * scale,
    top: offsetY + (yPct / 100) * naturalHeight * scale,
  }
}

/**
 * Screen-pixel -> image-pixel scale, for turning a fixed hit-test tolerance in
 * screen pixels into image pixels so the grab target stays finger-sized at every
 * canvas zoom level.
 */
export function displayScale(args: {
  rectWidth: number
  offsetWidth: number
  offsetHeight: number
  naturalWidth: number
  naturalHeight: number
}): number {
  const { rectWidth, offsetWidth, offsetHeight, naturalWidth, naturalHeight } = args
  if (offsetWidth <= 0 || naturalWidth <= 0 || naturalHeight <= 0) return 1
  const zoom = rectWidth > 0 ? rectWidth / offsetWidth : 1
  const contain = Math.min(offsetWidth / naturalWidth, offsetHeight / naturalHeight)
  return contain * zoom
}
